import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, catchError, of, map } from 'rxjs';

import { HttpMainService } from '../http-main.service';

import {
  ListModel,
  ListQuery,
  ProfileModel,
  RegisterCommand,
  ResetPasswordCommand,
  UserModel,
  UsersCommand,
} from '@ose/models';

import { APP_TOKEN, AppsEnum } from '@ose/enums';
import { toHttpParams } from '@ose/utils';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly baseUrl = '/member/';
  private readonly entity = 'users';

  private readonly httpMainService = inject(HttpMainService);
  private readonly app = inject(APP_TOKEN);

  private readonly _currentUser$ = new BehaviorSubject<UserModel | undefined>(
    undefined
  );

  readonly currentUser$ = this._currentUser$.asObservable();

  get currentUser(): UserModel | undefined {
    return this._currentUser$.value;
  }
  loadCurrentUser(): Observable<UserModel | undefined> {
    const resource = this.app === AppsEnum.CLIENT ? 'users' : 'admins';

    return this.httpMainService
      .get<UserModel>(`${this.baseUrl}v1/${resource}/current`)
      .pipe(
        tap((user) => {
          this._currentUser$.next(user);
        }),
        catchError((error) => {
          this._currentUser$.next(undefined);

          return of(undefined);
        })
      );
  }

  fetchCurrentUser(): void {
    this.loadCurrentUser().subscribe();
  }
  updateCurrentUserLanguage(language: string): Observable<UserModel> {
    const resource = this.app === AppsEnum.CLIENT ? 'users' : 'admins';

    return this.httpMainService
      .patch<UserModel>(`${this.baseUrl}v1/${resource}/current`, {
        language,
      })
      .pipe(
        tap((updatedUser) => {
          this._currentUser$.next(updatedUser);
        })
      );
  }

  register(model: RegisterCommand): Observable<UserModel> {
    return this.httpMainService.post<UserModel>(
      `${this.baseUrl}v1/users`,
      model
    );
  }

  resetPassword(model: ResetPasswordCommand): Observable<UserModel> {
    const resource = this.app === AppsEnum.CLIENT ? 'users' : 'admins';

    return this.httpMainService.patch<UserModel>(
      `${this.baseUrl}v1/${resource}`,
      model
    );
  }

  list(query: ListQuery): Observable<ListModel<UsersCommand>> {
    return this.httpMainService.get<ListModel<UsersCommand>>(
      `${this.baseUrl}v1/${this.entity}`,
      toHttpParams(query)
    );
  }

  getUserDetails(userId: string): Observable<UsersCommand> {
    return this.httpMainService.get<UsersCommand>(
      `${this.baseUrl}v1/${this.entity}/${userId}`
    );
  }

  hasConfirmedCards(): Observable<boolean> {
    return this.httpMainService
      .get<ListModel<unknown>>(
        '/finance/v1/cards',
        toHttpParams({
          filter: 'status:eq("CONFIRMED")',
        })
      )
      .pipe(
        map((response) => response.count > 0),
        catchError(() => of(false))
      );
  }

  download(userId: string): Observable<Blob> {
    return this.httpMainService.getFile(
      `${this.baseUrl}v1/profiles/${userId}/documents/single`
    );
  }
  getProfile(userId: string): Observable<ProfileModel> {
    return this.httpMainService.get<ProfileModel>(
      `${this.baseUrl}v1/profiles/${userId}`
    );
  }
}
